import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Badge,
  Avatar,
  Tabs,
  List,
  Typography,
  Switch
} from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useAuthStore } from '@/store/auth'
import { merchantVenueApi, venueAdminApi } from '@/services/api'

const { Search } = Input
const { Option } = Select
// const { TabPane } = Tabs
const { Title, Text } = Typography

interface Venue {
  id: number
  uuid: string
  venue_name: string
  venue_type: string
  merchant_id: number
  address: string
  province?: string
  city?: string
  district?: string
  phone?: string
  email?: string
  website?: string
  description?: string
  images?: string[]
  facilities?: string[]
  opening_hours?: any
  total_rooms?: number
  supports_ai_world?: boolean
  ai_device_capabilities?: any
  rating_avg?: number
  rating_count?: number
  status: 'active' | 'inactive' | 'draft' | 'maintenance'
  // 后端从 extra_metadata 提取到顶层
  local_mqtt_broker_address?: string
  local_ai_engine_address?: string
  local_asr_service_address?: string
  // 保留以兼容旧代码
  extra_metadata?: {
    local_mqtt_broker_address?: string
    local_ai_engine_address?: string
    local_asr_service_address?: string
  }
  created_at: string
  updated_at: string
}

interface Merchant {
  id: number
  company_name: string
}

const VenueManagement: React.FC = () => {
  // 使用auth store获取token
  const { token } = useAuthStore()

  // 场馆相关状态
  const [venues, setVenues] = useState<Venue[]>([])
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [loading, setLoading] = useState(false)
  const [merchantsLoading, setMerchantsLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null)
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null)
  const [venueStats, setVenueStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    draft: 0
  })
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  const [venueForm] = Form.useForm()

  // 管理员管理相关状态
  const [adminModalVisible, setAdminModalVisible] = useState(false)
  const [currentVenue, setCurrentVenue] = useState<Venue | null>(null)
  const [venueAdmins, setVenueAdmins] = useState<any[]>([])
  const [availableAdmins, setAvailableAdmins] = useState<any[]>([])
  const [adminLoading, setAdminLoading] = useState(false)

  // 获取商家列表（用于筛选）
  const fetchMerchants = async () => {
    setMerchantsLoading(true)
    try {
      const response = await merchantVenueApi.getMerchants()
      const merchantList = response.data?.items || []
      setMerchants(merchantList)
    } catch (error) {
      console.error('获取商户列表失败:', error)
      message.error('获取商户列表失败')
    } finally {
      setMerchantsLoading(false)
    }
  }

  // 获取场馆列表
  const fetchVenues = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
        search: searchText,
        status: statusFilter,
        include_deleted: includeDeleted
      }

      if (selectedMerchantId) {
        params.merchant_id = selectedMerchantId
      }

      const response = await merchantVenueApi.getVenues(params)
      const venueList = response.data?.items || []
      const total = response.data?.total || 0
      setVenues(venueList)
      setPagination(prev => ({ ...prev, total }))
      updateVenueStats(venueList)
    } catch (error) {
      console.error('获取场馆列表失败:', error)
      message.error('获取场馆列表失败')
      setVenues([])
    } finally {
      setLoading(false)
    }
  }

  // 更新场馆统计数据
  const updateVenueStats = (venues: Venue[]) => {
    const stats = {
      total: venues.length,
      active: venues.filter(v => v.status === 'active').length,
      inactive: venues.filter(v => v.status === 'inactive').length,
      draft: venues.filter(v => v.status === 'draft').length
    }
    setVenueStats(stats)
  }

  useEffect(() => {
    fetchMerchants()
  }, [])

  useEffect(() => {
    fetchVenues()
  }, [searchText, statusFilter, selectedMerchantId, includeDeleted, pagination.current])

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value)
  }

  // 处理状态筛选
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
  }

  // 处理商家筛选
  const handleMerchantFilter = (merchantId: string) => {
    setSelectedMerchantId(merchantId ? parseInt(merchantId) : null)
  }

  // 处理编辑场馆
  const handleEditVenue = async (venue: Venue) => {
    setModalVisible(true)
    try {
      // 获取完整场馆数据（包含 extra_metadata）
      const response = await merchantVenueApi.getVenue(venue.id)
      // apiClient 的响应拦截器已经解包了 error_code/data 格式
      // response.data 就是 venue 对象本身
      const fullVenue = response.data

      if (fullVenue) {
        setEditingVenue(fullVenue)
        venueForm.setFieldsValue({
          merchant_id: fullVenue.merchant_id,
          venue_name: fullVenue.venue_name,
          venue_type: fullVenue.venue_type,
          address: fullVenue.address,
          city: fullVenue.city,
          phone: fullVenue.phone,
          email: fullVenue.email,
          description: fullVenue.description,
          status: fullVenue.status,
          // 本地服务地址配置（后端已从 extra_metadata 提取到顶层字段）
          local_mqtt_broker_address: fullVenue.local_mqtt_broker_address,
          local_ai_engine_address: fullVenue.local_ai_engine_address,
          local_asr_service_address: fullVenue.local_asr_service_address,
        })
        setModalVisible(true)
      }
    } catch (error) {
      console.error('获取场馆详情失败:', error)
      message.error('获取场馆详情失败')
    }
  }

  // 处理删除场馆
  const handleDeleteVenue = async (venueId: number) => {
    if (!token) {
      message.error('未登录')
      return
    }

    try {
      await merchantVenueApi.deleteVenue(venueId)
      message.success('场馆删除成功')
      fetchVenues()
    } catch (error: any) {
      console.error('删除场馆失败:', error)
      message.error(error.response?.data?.message || error.message || '删除失败')
    }
  }

  // 管理场馆管理员
  const handleManageAdmins = async (venue: Venue) => {
    setCurrentVenue(venue)
    setAdminLoading(true)
    setAdminModalVisible(true)

    try {
      // 获取该场馆的管理员列表
      const response = await venueAdminApi.list({
        venue_id: venue.id.toString(),
        page: 1,
        page_size: 100,
      })
      setVenueAdmins(response.data.items || [])

      // 无场馆筛选时，后端会同时返回尚未绑定场馆的 venue_admin。
      const allAdminsResponse = await venueAdminApi.list({
        page: 1,
        page_size: 100,
      })
      setAvailableAdmins(allAdminsResponse.data.items || [])
    } catch (error: any) {
      console.error('获取管理员列表失败:', error)
      message.error('获取管理员列表失败')
    } finally {
      setAdminLoading(false)
    }
  }

  // 为场馆绑定管理员
  const handleBindAdminToVenue = async (userId: number) => {
    if (!currentVenue) return

    try {
      await venueAdminApi.bindVenue(userId, {
        merchant_id: currentVenue.merchant_id.toString(),
        venue_id: currentVenue.id.toString(),
        venue_name: currentVenue.venue_name,
        venue_address: currentVenue.address,
      })

      message.success('管理员绑定成功')
      await handleManageAdmins(currentVenue)
    } catch (error: any) {
      console.error('绑定管理员失败:', error)
      message.error(error.response?.data?.message || '绑定管理员失败')
    }
  }

  // 解绑管理员
  const handleUnbindAdmin = async (userId: number) => {
    try {
      await venueAdminApi.unbindVenue(userId)

      message.success('管理员解绑成功')
      if (currentVenue) {
        await handleManageAdmins(currentVenue)
      }
    } catch (error: any) {
      console.error('解绑管理员失败:', error)
      message.error(error.response?.data?.message || '解绑管理员失败')
    }
  }

  // 处理切换场馆状态
  const handleToggleVenueStatus = async (venue: Venue) => {
    if (!token) {
      message.error('未登录')
      return
    }

    const newStatus = venue.status === 'active' ? 'inactive' : 'active'

    try {
      await merchantVenueApi.updateVenue(venue.id, { status: newStatus })
      message.success(`场馆已${newStatus === 'active' ? '启用' : '停用'}`)
      fetchVenues()
    } catch (error: any) {
      console.error('切换状态失败:', error)
      message.error(error.response?.data?.message || error.message || '操作失败')
    }
  }

  // 获取场馆状态颜色
  const getVenueStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'inactive': return 'default'
      case 'draft': return 'processing'
      case 'maintenance': return 'warning'
      default: return 'default'
    }
  }

  // 获取场馆状态文本
  const getVenueStatusText = (status: string) => {
    switch (status) {
      case 'active': return '营业中'
      case 'inactive': return '已停用'
      case 'draft': return '草稿'
      case 'maintenance': return '维护中'
      default: return status
    }
  }

  const venueColumns: ColumnsType<Venue> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <Typography.Text copyable>{id}</Typography.Text>
    },
    {
      title: '场馆信息',
      key: 'info',
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar
            size={48}
            icon={<ShopOutlined />}
            className="mr-3"
            style={{ backgroundColor: '#1890ff' }}
          />
          <div>
            <div className="font-medium">[{record.id}] {record.venue_name}</div>
            <div className="text-sm text-gray-500">
              <Tag color={record.venue_type === 'entertainment' ? 'blue' : 'default'}>
                {record.venue_type || '未设置'}
              </Tag>
            </div>
          </div>
        </div>
      )
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      render: (address: string, record: Venue) => (
        <div>
          <div className="flex items-center mb-1">
            <EnvironmentOutlined className="mr-2 text-gray-400" />
            <span>{address}</span>
          </div>
          <div className="text-sm text-gray-500">{record.city}</div>
        </div>
      )
    },
    {
      title: '联系方式',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.phone && (
            <div className="flex items-center mb-1">
              <PhoneOutlined className="mr-2 text-gray-400" />
              <span>{record.phone}</span>
            </div>
          )}
          {record.email && (
            <div className="text-sm text-gray-500">{record.email}</div>
          )}
        </div>
      )
    },
    {
      title: '房间数',
      dataIndex: 'total_rooms',
      key: 'total_rooms',
      render: (totalRooms: number) => (
        <div>
          <div className="font-medium">{totalRooms || 0} 间</div>
          {totalRooms && (
            <div className="text-sm text-gray-500">可用房间</div>
          )}
        </div>
      )
    },
    {
      title: 'AI支持',
      key: 'ai_support',
      render: (_, record) => (
        record.supports_ai_world ? (
          <Tag color="blue">支持AI世界</Tag>
        ) : (
          <Tag color="default">不支持</Tag>
        )
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Badge
          status={getVenueStatusColor(status) as any}
          text={getVenueStatusText(status)}
        />
      )
    },
    {
      title: '管理员',
      key: 'admins',
      width: 150,
      render: (_: any, record: Venue) => {
        // 这里可以显示已绑定的管理员数量
        return (
          <Space>
            <Tag color="blue" icon={<TeamOutlined />}>
              管理员
            </Tag>
            <Button
              type="link"
              size="small"
              icon={<UserOutlined />}
              onClick={() => handleManageAdmins(record)}
            >
              设置
            </Button>
          </Space>
        )
      },
    },
    {
      title: '操作',
      key: 'action',
      dataIndex: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleVenueStatus(record)}
          >
            {record.status === 'active' ? '停用' : '启用'}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditVenue(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个场馆吗？"
            onConfirm={() => handleDeleteVenue(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">场馆管理</h1>
        <p className="text-gray-600">管理平台上的场馆信息，确保服务质量</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="总场馆数"
              value={venueStats.total}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="正常营业"
              value={venueStats.active}
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已停用"
              value={venueStats.inactive}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="草稿"
              value={venueStats.draft}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索和筛选 */}
      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-4">
            <Search
              placeholder="搜索场馆名称"
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <select
              className="border rounded px-3 py-2"
              style={{ width: 150 }}
              onChange={(e) => handleStatusFilter(e.target.value)}
              defaultValue=""
            >
              <option value="">全部状态</option>
              <option value="active">营业中</option>
              <option value="inactive">已停用</option>
              <option value="draft">草稿</option>
              <option value="maintenance">维护中</option>
            </select>
            <select
              className="border rounded px-3 py-2"
              style={{ width: 200 }}
              onChange={(e) => handleMerchantFilter(e.target.value)}
              defaultValue=""
              disabled={merchantsLoading}
            >
              <option value="">全部商家</option>
              {merchants.map(merchant => (
                <option key={merchant.id} value={merchant.id}>
                  [{merchant.id}] {merchant.company_name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <Switch
                size="small"
                checked={includeDeleted}
                onChange={setIncludeDeleted}
              />
              <span className="text-sm text-gray-500">显示已删除</span>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingVenue(null)
              venueForm.resetFields()
              setModalVisible(true)
            }}
          >
            新建场馆
          </Button>
        </div>

        {/* 场馆列表 */}
        <Table
          columns={venueColumns}
          dataSource={venues}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ current: page, pageSize: pageSize || 10, total: prev.total }))
            },
          }}
        />
      </Card>

      {/* 场馆模态框 */}
      <Modal
        title={
          <span>
            {editingVenue ? '编辑场馆' : '添加场馆'}
            {editingVenue && (
              <Typography.Text type="secondary" className="ml-3 text-sm">
                ID: {editingVenue.id}
              </Typography.Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingVenue(null)
          venueForm.resetFields()
        }}
        width={600}
        footer={null}
      >
        <Form
          form={venueForm}
          layout="vertical"
          onFinish={async (formValues) => {
            try {
              console.log('场馆数据:', formValues)

              if (!token) {
                message.error('未登录，请先登录')
                return
              }

              const requestBody: any = {
                venue_name: formValues.venue_name,
                venue_type: formValues.venue_type,
                address: formValues.address || '待填写',
                city: formValues.city,
                phone: formValues.phone,
                email: formValues.email,
                description: formValues.description,
              }

              // 只有创建时才需要 merchant_id
              if (!editingVenue && formValues.merchant_id) {
                requestBody.merchant_id = formValues.merchant_id
              }

              // 如果是编辑且有状态字段
              if (editingVenue && formValues.status) {
                requestBody.status = formValues.status
              }

              // 本地服务地址配置（存储在 extra_metadata 中）
              const hasLocalAddresses = formValues.local_mqtt_broker_address ||
                formValues.local_ai_engine_address ||
                formValues.local_asr_service_address
              if (hasLocalAddresses) {
                requestBody.extra_metadata = {
                  local_mqtt_broker_address: formValues.local_mqtt_broker_address || null,
                  local_ai_engine_address: formValues.local_ai_engine_address || null,
                  local_asr_service_address: formValues.local_asr_service_address || null,
                }
              }

              // 使用 merchantVenueApi 进行 API 调用
              try {
                if (editingVenue) {
                  await merchantVenueApi.updateVenue(editingVenue.id, requestBody)
                  message.success('场馆更新成功')
                } else {
                  await merchantVenueApi.createVenue(requestBody)
                  message.success('场馆创建成功')
                }
                setModalVisible(false)
                setEditingVenue(null)
                venueForm.resetFields()
                fetchVenues()
              } catch (error: any) {
                console.error('场馆操作失败:', error)
                message.error(error.response?.data?.message || error.message || '操作失败')
              }
            } catch (error) {
              console.error('场馆操作失败:', error)
              message.error('网络错误，请重试')
            }
          }}
        >
          {/* 商家选择（新建时显示，编辑时隐藏） */}
          {!editingVenue && (
            <Form.Item
              label="所属商家"
              name="merchant_id"
              rules={[{ required: true, message: '请选择所属商家' }]}
            >
              <Select
                placeholder="选择商家"
                loading={merchantsLoading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as any)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {merchants.map(merchant => (
                  <Option key={merchant.id} value={merchant.id}>
                    [{merchant.id}] {merchant.company_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* 编辑时的隐藏字段：商户ID */}
          {editingVenue && (
            <Form.Item name="merchant_id" style={{ display: 'none' }}>
              <Input type="hidden" />
            </Form.Item>
          )}

          <Form.Item
            label="场馆名称"
            name="venue_name"
            rules={[{ required: true, message: '请输入场馆名称' }]}
          >
            <Input placeholder="输入场馆名称" />
          </Form.Item>

          <Form.Item
            label="场馆类型"
            name="venue_type"
          >
            <Select placeholder="选择场馆类型">
              <Option value="entertainment">娱乐场所</Option>
              <Option value="conference">会议中心</Option>
              <Option value="exhibition">展览馆</Option>
              <Option value="theater">剧院</Option>
              <Option value="stadium">体育场</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="地址"
            name="address"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input placeholder="输入详细地址" />
          </Form.Item>

          <Form.Item
            label="城市"
            name="city"
          >
            <Input placeholder="输入城市" />
          </Form.Item>

          <Form.Item
            label="联系电话"
            name="phone"
          >
            <Input placeholder="输入联系电话" />
          </Form.Item>

          <Form.Item
            label="邮箱"
            name="email"
          >
            <Input placeholder="输入邮箱地址" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <Input.TextArea rows={3} placeholder="输入场馆描述" />
          </Form.Item>

          {/* 本地服务地址配置 */}
          <div style={{ marginBottom: 16, padding: '12px', background: '#f5f5f5', borderRadius: 4 }}>
            <Typography.Text strong>本地服务地址配置</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              配置场馆本地的 MQTT、AI Engine 和 ASR Service 地址
            </Typography.Text>

            <Form.Item
              label="MQTT Broker 地址"
              name="local_mqtt_broker_address"
              style={{ marginBottom: 8 }}
            >
              <Input placeholder="如 mqtt://192.168.1.100:1884" />
            </Form.Item>

            <Form.Item
              label="AI Engine 地址"
              name="local_ai_engine_address"
              style={{ marginBottom: 8 }}
            >
              <Input placeholder="如 http://192.168.1.100:8030" />
            </Form.Item>

            <Form.Item
              label="ASR Service 地址"
              name="local_asr_service_address"
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="如 ws://192.168.1.100:8000" />
            </Form.Item>
          </div>

          {/* 状态选择（仅编辑模式显示） */}
          {editingVenue && (
            <Form.Item
              label="场馆状态"
              name="status"
            >
              <Select placeholder="选择场馆状态">
                <Option value="active">营业中</Option>
                <Option value="inactive">已停用</Option>
                <Option value="draft">草稿</Option>
                <Option value="maintenance">维护中</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingVenue ? '更新' : '创建'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false)
                setEditingVenue(null)
                venueForm.resetFields()
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 管理员管理弹窗 */}
      <Modal
        title={
          <Space>
            <TeamOutlined />
            <span>场馆管理员设置 - {currentVenue?.venue_name}</span>
          </Space>
        }
        open={adminModalVisible}
        onCancel={() => {
          setAdminModalVisible(false)
          setCurrentVenue(null)
          setVenueAdmins([])
          setAvailableAdmins([])
        }}
        footer={null}
        width={700}
      >
        {adminLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Typography.Text>加载中...</Typography.Text>
          </div>
        ) : (
          <>
            {/* 已绑定的管理员列表 */}
            <div style={{ marginBottom: 24 }}>
              <Title level={5}>已绑定管理员 ({venueAdmins.length})</Title>
              {venueAdmins.length === 0 ? (
                <Typography.Text type="secondary">暂无绑定的管理员</Typography.Text>
              ) : (
                <List
                  dataSource={venueAdmins}
                  renderItem={(admin) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          title="确认解绑"
                          description={`确定要解绑管理员 ${admin.username} 吗？`}
                          onConfirm={() => handleUnbindAdmin(admin.id)}
                          okText="确认"
                          cancelText="取消"
                        >
                          <Button type="link" danger size="small">
                            解绑
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={admin.username}
                        description={
                          <Space>
                            {admin.email && <Tag>{admin.email}</Tag>}
                            {admin.phone && <Tag>{admin.phone}</Tag>}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </div>

            {/* 添加新管理员 */}
            <div>
              <Title level={5}>添加管理员</Title>
              <Select
                mode="multiple"
                placeholder="选择要绑定的管理员（可多选，只显示有 venue_admin 角色的用户）"
                style={{ width: '100%' }}
                onChange={async (userIds: number[]) => {
                  if (!userIds || userIds.length === 0) return

                  // 批量绑定
                  try {
                    const results = await Promise.all(
                      userIds.map(userId => handleBindAdminToVenue(userId))
                    )
                    message.success(`成功绑定 ${results.length} 个管理员`)
                  } catch (error) {
                    message.error('部分管理员绑定失败')
                  }
                }}
                options={availableAdmins
                  .filter((admin) => {
                    // 过滤掉已经绑定到当前场馆的管理员
                    return !venueAdmins.find((va) => va.id === admin.id)
                  })
                  .map((admin) => ({
                    value: admin.id,
                    label: `[${admin.id}] ${admin.username} (${admin.email || '无邮箱'})${admin.venue_id
                        ? ` - 已绑定到场馆 ${admin.venue_name || admin.venue_id}`
                        : ''
                      }`,
                  }))}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

export default VenueManagement
